/**
 * Localization Discovery
 *
 * Detects the visitor's optimal country/language using their browser context
 * and IP address via Shopify's browsing_context_suggestions Ajax API.
 *
 * Reference: https://shopify.dev/docs/storefronts/themes/markets/localization-discovery
 */

/**
 * Fetches browsing context suggestions from Shopify's Ajax API.
 * Uses the visitor's IP and browser settings to recommend a country/language.
 *
 * @returns {Promise<{detected_values: object, suggestions: Array}>}
 */
async function getBrowsingContextSuggestions() {
  const params = new URLSearchParams({
    "country[enabled]": "true",
    "country[exclude]": window.Shopify.country,
    "language[enabled]": "true",
    "language[exclude]": window.Shopify.language,
  });

  const response = await fetch(
    `${window.Shopify.routes.root}browsing_context_suggestions.json?${params}`,
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch browsing context suggestions: ${response.status}`,
    );
  }

  return response.json();
}

/**
 * Submits the localization form to update the visitor's country and language.
 *
 * @param {object} params
 * @param {string} params.country - Country code (e.g. "CA")
 * @param {string} params.language - Language code (e.g. "fr")
 */
function updateLocalization({ country, language }) {
  const formId = crypto.randomUUID();
  const formHtml = `
    <form id="${formId}" action="/localization" method="POST" hidden>
      <input name="_method" value="PUT" />
      <input name="country_code" value="${country}" />
      <input name="language_code" value="${language}" />
    </form>
  `;
  document.body.insertAdjacentHTML("beforeend", formHtml);
  document.getElementById(formId).submit();
}

/**
 * Detects the visitor's optimal localization and, if a high-confidence
 * suggestion exists, updates their storefront context.
 *
 * Only redirects when confidence is 1 (100%) to avoid false positives.
 * Does not override the visitor's explicit choice (stored in sessionStorage).
 */
async function detectAndApplyLocalization() {
  // Respect explicit visitor override — don't auto-redirect again this session
  if (sessionStorage.getItem("localization_dismissed")) return;

  try {
    const { suggestions } = await getBrowsingContextSuggestions();

    if (!suggestions || suggestions.length === 0) return;

    const best = suggestions[0];
    if (best.confidence < 1) return;

    const country = best.parts?.country?.handle;
    const language = best.parts?.language?.handle;

    if (!country && !language) return;

    updateLocalization({
      country: country || window.Shopify.country,
      language: language || window.Shopify.language,
    });
  } catch (err) {
    console.error("[LocalizationDiscovery]", err);
  }
}

/**
 * Fetches the visitor's detected country via browsing context suggestions,
 * sets window.country, and adds a country-{code} class to <body>.
 * Replaces the legacy ipInfo / ipinfo.io approach.
 *
 * For testing, append ?preview_country=NZ (or any country code) to the URL.
 */
async function initCountry() {
  try {
    // Dev override: ?preview_country=NZ
    const previewCountry = new URLSearchParams(window.location.search).get(
      "preview_country",
    );
    if (previewCountry) {
      const code = previewCountry.toUpperCase();
      window.country = code;
      document.body.classList.add(`country-${code.toLowerCase()}`);
      console.info(`[LocalizationDiscovery] preview_country override: ${code}`);
      return;
    }

    const { detected_values } = await getBrowsingContextSuggestions();
    const countryHandle = detected_values?.country?.handle;
    if (!countryHandle) return;
    window.country = countryHandle;
    document.body.classList.add(`country-${countryHandle.toLowerCase()}`);
  } catch (err) {
    console.error("[LocalizationDiscovery] initCountry:", err);
  }
}

document.addEventListener("DOMContentLoaded", initCountry);

/**
 * Checks if the visitor's detected country differs from the current storefront
 * localization (NZ ↔ US) and opens the redirect modal if so.
 * Only runs on the homepage; called from theme.liquid.
 *
 * @param {string} localizationIsoCode - Current storefront country ISO code (from Liquid)
 */
async function showCountryRedirectModal(localizationIsoCode) {
  try {
    // Dev override: ?preview_country=NZ
    const previewCountry = new URLSearchParams(window.location.search).get(
      "preview_country",
    );

    let countryName, countryHandle;
    if (previewCountry) {
      countryHandle = previewCountry.toUpperCase();
      countryName = countryHandle;
    } else {
      const { detected_values } = await getBrowsingContextSuggestions();
      countryName = detected_values?.country?.name;
      countryHandle = detected_values?.country?.handle;
    }

    if (!countryHandle) return;

    const shouldShowModal =
      (localizationIsoCode === "NZ" && countryHandle === "US") ||
      (localizationIsoCode === "US" && countryHandle === "NZ");

    if (shouldShowModal) {
      const displayFrom = countryHandle === "US" ? "the US" : "NZ";
      const displayTo = countryHandle === "US" ? "US" : "NZ";
      document.getElementById("popup-country-select-heading").innerHTML =
        `It seems you're in ${displayFrom}. <br /> Redirect to the ${displayTo} site?`;
      document.getElementById("header-localization-form-currency-popup").open =
        true;
    }

    return countryHandle;
  } catch (err) {
    console.error("[LocalizationDiscovery] showCountryRedirectModal:", err);
  }
}

window.LocalizationDiscovery = {
  getBrowsingContextSuggestions,
  updateLocalization,
  detectAndApplyLocalization,
  initCountry,
  showCountryRedirectModal,
};
